import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import api from '../../services/api';
import { expandRecurringMeetings } from '../CalendarComponent/calendarUtils';

// Initialize dayjs plugins
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

/**
 * @typedef {object} Attendee
 * @property {number} id - The ID of the attendee.
 * @property {string} full_name - The full name of the attendee.
 * @property {string} [avatar_url] - The URL of the attendee's avatar.
 */

/**
 * @typedef {object} Meeting
 * @property {number|string} id - The unique identifier for the meeting.
 * @property {string} meeting_name - The name of the meeting.
 * @property {string} url - The URL for the meeting.
 * @property {string} start_date - The start date of the meeting (YYYY-MM-DD).
 * @property {string} end_date - The end date of the meeting (YYYY-MM-DD).
 * @property {string} start_time - The start time of the meeting (HH:mm:ss).
 * @property {string} end_time - The end time of the meeting (HH:mm:ss).
 * @property {boolean} is_recurring - Flag indicating if the meeting is recurring.
 * @property {boolean} is_weekly_recurring - Flag indicating if the meeting recurs weekly.
 * @property {number[]} recurring_days - Array of day numbers (0=Sunday, 1=Monday, etc.) for weekly recurring meetings.
 * @property {Attendee[]} attendees - An array of attendees.
 * @property {number} created_by - The ID of the user who created the meeting.
 * @property {boolean} [isRecurringInstance] - Internal flag for recurring instances.
 * @property {number|string} [originalMeetingId] - Internal ID for recurring instances.
 */

/**
 * CalendarView component to display and manage meetings.
 * @component
 * @param {object} props - The component props.
 * @param {Meeting[]} [props.meetings=[]] - Array of meeting objects.
 * @param {function} props.onEditMeeting - Callback function triggered when a meeting edit is initiated.
 * @param {function} props.onMeetingDeleted - Callback function triggered after a meeting is deleted.
 * @returns {JSX.Element} The CalendarView component.
 */
const CalendarView = ({ meetings = [], onEditMeeting, onMeetingDeleted }) => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [view, setView] = useState('month'); // 'month', 'week', 'day'
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /**
   * Processes meetings to include instances of recurring meetings within their date range.
   * @type {Meeting[]}
   */
  const processedMeetings = useMemo(
    () => expandRecurringMeetings(meetings),
    [meetings],
  );

  /**
   * Handles the change of calendar view (month, week, day).
   * @param {React.MouseEvent<HTMLElement>} event - The event source of the callback.
   * @param {string | null} newView - The new view type ('month', 'week', 'day').
   * @returns {void}
   */
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  /**
   * Navigates to the previous period (month, week, or day) based on the current view.
   * @returns {void}
   */
  const handlePrevious = () => {
    setCurrentDate(currentDate.subtract(1, view));
  };

  /**
   * Navigates to the next period (month, week, or day) based on the current view.
   * @returns {void}
   */
  const handleNext = () => {
    setCurrentDate(currentDate.add(1, view));
  };

  /**
   * Sets the current date to today.
   * @returns {void}
   */
  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  /**
   * Handles clicking on a meeting, setting it as selected and opening the details dialog.
   * @param {Meeting} meeting - The meeting object that was clicked.
   * @returns {void}
   */
  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(meeting);
    setIsDialogOpen(true);
  };

  /**
   * Filters and returns meetings scheduled for a specific date, considering recurrence rules.
   * @param {dayjs.Dayjs} date - The date for which to retrieve meetings.
   * @returns {Meeting[]} An array of meetings for the given date.
   */
  const getMeetingsForDate = (date) => {
    // Note: This function uses the original `meetings` prop, not `processedMeetings`
    // because `processedMeetings` expands recurring meetings into daily instances,
    // which is more suitable for rendering directly on a calendar grid cell
    // that represents a single day. This function, however, might be used
    // in contexts where the original meeting object with its full recurrence info is needed.
    // For the current rendering logic in month/week/day views, `processedMeetings`
    // might be more direct if we iterate over `processedMeetings` and check if `meeting.start_date` matches `date`.
    // However, the current implementation filters the original `meetings` list.
    // Let's adjust to use `processedMeetings` for consistency with how they are displayed.
    
    // Re-filtering `processedMeetings` for the specific date.
    // This is slightly redundant if `processedMeetings` is already structured per day,
    // but it ensures correct filtering if `getMeetingsForDate` is called independently.
    return processedMeetings.filter((meeting) => {
      const meetingStartDate = dayjs(meeting.start_date);
      return meetingStartDate.isSame(date, 'day');
    });
  };


  /**
   * Generates an array of time slots for day and week views (00:00 to 23:00).
   * @type {dayjs.Dayjs[]}
   */
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let i = 0; i < 24; i++) {
      slots.push(dayjs().startOf('day').add(i, 'hour'));
    }
    return slots;
  }, []);

  /**
   * Generates an array of dayjs objects for all days to be displayed in the current month view.
   * This includes days from the previous and next months to fill the week rows.
   * @type {dayjs.Dayjs[]}
   */
  const monthDays = useMemo(() => {
    const startOfMonth = currentDate.startOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = startOfMonth.endOf('month').endOf('week');

    const days = [];
    let day = startDate;

    while (day.isSameOrBefore(endDate, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }

    return days;
  }, [currentDate]);

  /**
   * Generates an array of dayjs objects for the 7 days of the current week view.
   * @type {dayjs.Dayjs[]}
   */
  const weekDays = useMemo(() => {
    const days = [];
    const startOfWeek = currentDate.startOf('week');

    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }

    return days;
  }, [currentDate]);

  /**
   * Renders the calendar in month view.
   * @returns {JSX.Element} The month view JSX.
   */
  const renderMonthView = () => (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}
    >
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <Box key={day} sx={{ p: 1, textAlign: 'center', fontWeight: 'bold' }}>
          {day}
        </Box>
      ))}

      {monthDays.map((day) => (
        <Paper
          key={day.format()}
          elevation={0}
          sx={{
            p: 1,
            height: 100,
            bgcolor: day.isSame(dayjs(), 'day')
              ? 'action.hover'
              : 'background.paper',
            opacity: day.isSame(currentDate, 'month') ? 1 : 0.5,
            overflow: 'auto',
          }}
        >
          <Typography variant="caption">{day.format('D')}</Typography>
          {getMeetingsForDate(day).map((meeting) => (
            <Tooltip
              key={`${meeting.id}-${meeting.start_date}`} // Use originalMeetingId if it's an instance
              title={`${meeting.meeting_name} (${dayjs(
                `${meeting.start_date}T${meeting.start_time}` // Use instance start_date
              ).format('HH:mm')})${(meeting.is_recurring || meeting.is_weekly_recurring) ? (meeting.is_weekly_recurring ? ' - Weekly Recurring' : ' - Daily Recurring') : ''}`}
            >
              <Box
                onClick={() => handleMeetingClick(meeting)}
                sx={{
                  bgcolor: (meeting.is_recurring || meeting.is_weekly_recurring)
                    ? 'secondary.main'
                    : 'primary.main',
                  color: 'white',
                  p: 0.5,
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    bgcolor: (meeting.is_recurring || meeting.is_weekly_recurring) ? 'white' : 'transparent', // Differentiate recurring visual
                    display: (meeting.is_recurring || meeting.is_weekly_recurring) ? 'block' : 'none',
                  }}
                />
                {dayjs(
                  `${meeting.start_date}T${meeting.start_time}` // Use instance start_date
                ).format('HH:mm')}{' '}
                - {meeting.meeting_name}
              </Box>
            </Tooltip>
          ))}
        </Paper>
      ))}
    </Box>
  );

  /**
   * Renders the calendar in week view.
   * @returns {JSX.Element} The week view JSX.
   */
  const renderWeekView = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '100px repeat(7, 1fr)',
        gap: 1,
      }}
    >
      <Box /> {/* Empty cell for time column header */}
      {weekDays.map((day) => (
        <Box
          key={day.format()}
          sx={{
            p: 1,
            textAlign: 'center',
            fontWeight: 'bold',
            bgcolor: day.isSame(dayjs(), 'day')
              ? 'action.hover'
              : 'transparent',
          }}
        >
          <Typography variant="caption" display="block">
            {day.format('ddd')}
          </Typography>
          <Typography variant="body2">{day.format('D')}</Typography>
        </Box>
      ))}

      {timeSlots.map((time) => (
        <React.Fragment key={time.format()}>
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            {time.format('HH:mm')}
          </Box>
          {weekDays.map((day) => {
            const dayMeetings = getMeetingsForDate(day).filter((meeting) => {
              const meetingHour = dayjs(
                `${meeting.start_date}T${meeting.start_time}`
              ).hour();
              return meetingHour === time.hour();
            });

            return (
              <Box
                key={day.format()}
                sx={{
                  p: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                  minHeight: '60px',
                }}
              >
                {dayMeetings.map((meeting) => (
                  <Tooltip
                    key={`${meeting.id}-${meeting.start_date}`}
                    title={`${meeting.meeting_name}${
                      (meeting.is_recurring || meeting.is_weekly_recurring) ? (meeting.is_weekly_recurring ? ' (Weekly Recurring)' : ' (Daily Recurring)') : ''
                    }`}
                  >
                    <Box
                      onClick={() => handleMeetingClick(meeting)}
                      sx={{
                        bgcolor: (meeting.is_recurring || meeting.is_weekly_recurring)
                          ? 'secondary.main'
                          : 'primary.main',
                        color: 'white',
                        p: 0.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {(meeting.is_recurring || meeting.is_weekly_recurring) && (
                        <Box
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            bgcolor: 'white',
                          }}
                        />
                      )}
                      {meeting.meeting_name}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );

  /**
   * Renders the calendar in day view.
   * @returns {JSX.Element} The day view JSX.
   */
  const renderDayView = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 1 }}>
      <Box /> {/* Empty cell for time column header */}
      <Box sx={{ p: 1, textAlign: 'center', fontWeight: 'bold' }}>
        {currentDate.format('dddd, MMMM D')}
      </Box>

      {timeSlots.map((time) => {
        const meetingsInSlot = getMeetingsForDate(currentDate).filter(
          (meeting) =>
            dayjs(
              `${meeting.start_date}T${meeting.start_time}`
            ).hour() === time.hour()
        );

        return (
          <React.Fragment key={time.format()}>
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
              {time.format('HH:mm')}
            </Box>
            <Box
              sx={{
                p: 1,
                borderTop: 1,
                borderColor: 'divider',
                minHeight: '60px',
              }}
            >
              {meetingsInSlot.map((meeting) => (
                <Tooltip
                  key={meeting.isRecurringInstance ? `${meeting.originalMeetingId}-${meeting.start_date}` : meeting.id}
                  title={`${meeting.meeting_name} (${dayjs(
                    `${meeting.start_date}T${meeting.start_time}`
                  ).format('HH:mm')} - ${dayjs(
                    `${meeting.start_date}T${meeting.end_time}`
                  ).format('HH:mm')})`}
                >
                  <Box
                    onClick={() => handleMeetingClick(meeting)}
                    sx={{
                      bgcolor: (meeting.is_recurring || meeting.is_weekly_recurring) ? 'secondary.main' : 'primary.main',
                      color: 'white',
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      {meeting.meeting_name}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {dayjs(
                        `${meeting.start_date}T${meeting.start_time}`
                      ).format('HH:mm')}{' '}
                      -
                      {dayjs(
                        `${meeting.start_date}T${meeting.end_time}`
                      ).format('HH:mm')}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );

  /**
   * Renders the dialog for displaying meeting details and actions.
   * @returns {JSX.Element | null} The meeting dialog JSX, or null if no meeting is selected.
   */
  const renderMeetingDialog = () => {
    const loggedInUser = localStorage.getItem('user');
  
    /**
     * Handles the edit action for the selected meeting.
     * Prepares meeting data and calls the onEditMeeting prop.
     * @returns {void}
     */
    const handleEditMeeting = () => {
      // Populate formData with selectedMeeting data and open the dialog
      const originalMeeting = selectedMeeting.isRecurringInstance 
        ? meetings.find(m => m.id === selectedMeeting.originalMeetingId)
        : selectedMeeting;
        
      const meetingData = {
        id: selectedMeeting.originalMeetingId || selectedMeeting.id, // Use original ID for recurring instances
        meeting_name: selectedMeeting.meeting_name,
        url: selectedMeeting.url,
        // For editing, use the original start/end dates of the series if it's a recurring meeting instance
        start_date: dayjs(originalMeeting?.start_date || selectedMeeting.start_date),
        end_date: dayjs(originalMeeting?.end_date || selectedMeeting.end_date),
        start_time: selectedMeeting.start_time,
        end_time: selectedMeeting.end_time,
        is_recurring: selectedMeeting.is_recurring || selectedMeeting.is_weekly_recurring,
        is_weekly_recurring: selectedMeeting.is_weekly_recurring,
        recurring_days: selectedMeeting.recurring_days || [],
        attendees: selectedMeeting.attendees,
      };
      onEditMeeting(meetingData); // Pass data to parent component
      setIsDialogOpen(false);
    };
  
    /**
     * Handles the deletion of the selected meeting.
     * Calls the API to delete the meeting and then the onMeetingDeleted prop.
     * @async
     * @returns {Promise<void>}
     */
    const handleDeleteMeeting = async () => {
      try {
        // For recurring instances, we might want to ask if user wants to delete series or instance.
        // For simplicity, deleting the original meeting ID will delete the series.
        const idToDelete = selectedMeeting.originalMeetingId || selectedMeeting.id;
        await api.delete(`meetings/${idToDelete}/`);
        onMeetingDeleted(); // Notify parent to refresh meetings
        setIsDialogOpen(false);
      } catch (error) {
        console.error('Error deleting meeting:', error);
      }
    };
  
    return (
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedMeeting && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <VideoCallIcon color="primary" />
                {selectedMeeting.meeting_name}
                {(selectedMeeting.is_recurring || selectedMeeting.is_weekly_recurring) && (
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'secondary.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      ml: 1,
                    }}
                  >
                    {selectedMeeting.is_weekly_recurring ? 'Weekly Recurring' : 'Daily Recurring'}
                  </Typography>
                )}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Date:</strong>{' '}
                  {/* Display the specific instance date */}
                  {dayjs(selectedMeeting.start_date).format('MMMM D, YYYY')}
                </Typography>
                {(selectedMeeting.is_recurring || selectedMeeting.is_weekly_recurring) && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Recurring Pattern:</strong>{' '}
                    {selectedMeeting.is_weekly_recurring 
                      ? `Weekly on ${selectedMeeting.recurring_days?.map(day => {
                          const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                          return dayNames[day];
                        }).join(', ') || 'selected days'}` 
                      : 'Daily'
                    }
                  </Typography>
                )}
                {(selectedMeeting.is_recurring || selectedMeeting.is_weekly_recurring) && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Recurring Period:</strong>{' '}
                    {/* Show original series start/end for context */}
                    {dayjs(meetings.find(m => m.id === selectedMeeting.originalMeetingId)?.start_date || selectedMeeting.start_date).format('MMMM D, YYYY')} -{' '}
                    {dayjs(meetings.find(m => m.id === selectedMeeting.originalMeetingId)?.end_date || selectedMeeting.end_date).format('MMMM D, YYYY')}
                  </Typography>
                )}
                <Typography variant="body1" gutterBottom>
                  <strong>Time:</strong>{' '}
                  {dayjs(
                    `${selectedMeeting.start_date}T${selectedMeeting.start_time}`
                  ).format('HH:mm')}{' '}
                  -{' '}
                  {dayjs(
                    `${selectedMeeting.start_date}T${selectedMeeting.end_time}`
                  ).format('HH:mm')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Meeting URL:</strong> {selectedMeeting.url}
                </Typography>
                <Box display="flex" flexDirection="column" gap={1} mt={1}>
                  <Typography variant="body1">
                    <strong>Attendees:</strong> {selectedMeeting.attendees.length}
                  </Typography>
                  {/* Attendee avatars and names can be added here if needed */}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.open(selectedMeeting.url, '_blank')}
                startIcon={<VideoCallIcon />}
              >
                Join Meeting
              </Button>
              {/* Ensure created_by check uses the original meeting's creator for instances */}
              {(() => {
                const originalMeeting = selectedMeeting.isRecurringInstance 
                  ? meetings.find(m => m.id === selectedMeeting.originalMeetingId)
                  : selectedMeeting;
                return Number(loggedInUser) === (originalMeeting?.created_by || selectedMeeting.created_by);
              })() && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleEditMeeting}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleDeleteMeeting}
                  >
                    Delete
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    );
  };
  

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ButtonGroup variant="outlined" size="small">
          <IconButton onClick={handlePrevious} aria-label="Previous period">
            <ChevronLeftIcon />
          </IconButton>
          <Button onClick={handleToday}>Today</Button>
          <IconButton onClick={handleNext} aria-label="Next period">
            <ChevronRightIcon />
          </IconButton>
        </ButtonGroup>

        <Typography variant="h6">
          {currentDate.format(view === 'month' ? 'MMMM YYYY' : 'MMMM D, YYYY')}
        </Typography>

        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          size="small"
          aria-label="Calendar view"
        >
          <ToggleButton value="month" aria-label="Month view">Month</ToggleButton>
          <ToggleButton value="week" aria-label="Week view">Week</ToggleButton>
          <ToggleButton value="day" aria-label="Day view">Day</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
      </Box>

      {renderMeetingDialog()}
    </Paper>
  );
};

export default CalendarView;
